/** Angular imports */
import { Component, computed, inject, signal } from '@angular/core';

/** Core imports */
import { CreateUserStepsService } from '@core';

/** Local imports */
import { PREVIEW_DETAILS_LAYOUT, SUBMIT_NOTES } from '@features/users/constants';
import {
    AllKeys,
    CreateUserFlowDraft,
    PreviewFormField,
    SelectDealerStepRoles,
    SelectDealerStepSchema,
    UserRoles,
} from '@features/users/interfaces';

/** Component pantry imports */
import { Checkbox } from '@ntv360/component-pantry';

/** Local components */
import { AvatarUploaderComponent } from '@shared';
import { citySelectionKeyToApiCityName } from '@shared/utils';

@Component({
    selector: 'app-preview-details',
    imports: [Checkbox, AvatarUploaderComponent],
    templateUrl: './preview-details.component.html',
    styleUrl: './preview-details.component.scss',
})
export class PreviewDetailsComponent {
    /** Default masked text for password preview. */
    private static readonly MASKED_PASSWORD = '******';

    /**
     * Inject create user service to access the draft data
     */
    private createUserStepsService = inject(CreateUserStepsService);

    /**
     * Layout constants
     */
    protected submitNotes = SUBMIT_NOTES;

    /**
     * Selected user role to be created
     */
    protected selectedUserType = this.createUserStepsService.selectedUserType;

    /**
     * Confirmation checkbox state
     */
    protected isConfirmationChecked = this.createUserStepsService.isConfirmationChecked;

    /**
     * Form fields layout based on selected user role
     */
    protected FORM_FIELDS =
        PREVIEW_DETAILS_LAYOUT[this.selectedUserType()?.id as keyof typeof PREVIEW_DETAILS_LAYOUT];

    /** Render model for preview rows to keep template logic concise. */
    public readonly previewFieldRows = computed(() => {
        const fields = this.FORM_FIELDS?.fields ?? [];
        const isDealer = this.selectedUserType()?.id === 'dealer';
        const rows: { fields: PreviewFormField[]; fullWidth?: boolean }[] = [];

        for (const field of fields) {
            if (isDealer && field.fieldName === 'city') {
                const groupedFields = ['city', 'state', 'region']
                    .map((fieldName) => fields.find((item) => item.fieldName === fieldName))
                    .filter((item): item is PreviewFormField => Boolean(item));

                rows.push({ fields: groupedFields, fullWidth: true });
                continue;
            }

            if (isDealer && (field.fieldName === 'state' || field.fieldName === 'region')) {
                continue;
            }

            rows.push({ fields: [field], fullWidth: field.fullWidth });
        }

        return rows;
    });

    /**
     * Access draft data from the service.
     */
    protected draftData = this.createUserStepsService.draft;

    /**
     * Local profile picture preview for cross-step display before final submit.
     */
    protected profilePicturePreview = this.createUserStepsService.profilePicturePreview;

    /** Toggles whether password is visible in preview details. */
    protected isPasswordVisible = signal(false);

    /**
     * Access the selected dealers array IF present
     */
    protected selectedDealers = computed(() => {
        const draft = this.createUserStepsService.draft();
        return (
            (draft as Partial<SelectDealerStepSchema>)?.['select-dealer']?.['selectedDealers'] ?? []
        );
    });

    /**
     * @protected
     * @description Retrieves the field value from the draft data based on a given fieldname.
     * @param fieldName - Field name to get the value from
     * @param step - Step key where the field value is stored
     * @returns The value of the field
     */
    protected getFieldValue(
        fieldName: string,
        step: AllKeys<CreateUserFlowDraft[Exclude<UserRoles, 'none'>]>,
    ) {
        const draft = this.createUserStepsService.getDraft(
            this.selectedUserType()?.id as Exclude<UserRoles, 'none'>,
        );
        return (draft as any)[step][fieldName];
    }

    /**
     * @protected
     * @description Retrieves the display-safe value for a field. Password is masked by default unless toggled visible.
     * @param fieldName - Field name to get the value from
     * @param step - Step key where the field value is stored
     * @returns Display value for preview
     */
    protected getDisplayFieldValue(
        fieldName: string,
        step: AllKeys<CreateUserFlowDraft[Exclude<UserRoles, 'none'>]>,
    ): string {
        const fieldValue = this.getFieldValue(fieldName, step);
        const value = fieldValue === undefined || fieldValue === null ? '' : String(fieldValue);

        if (fieldName === 'password') {
            return this.isPasswordVisible() ? value : PreviewDetailsComponent.MASKED_PASSWORD;
        }

        if (fieldName === 'city') {
            return citySelectionKeyToApiCityName(value);
        }

        return value;
    }

    /**
     * @protected
     * @description Toggles password visibility in preview details.
     * @returns {void}
     */
    protected onTogglePasswordVisibility(): void {
        this.isPasswordVisible.update((currentVisibility) => !currentVisibility);
    }

    /**
     * @protected
     * @description Removes a selected dealer from the draft.
     * @param value - Dealer value key to remove
     * @returns {void}
     */
    protected onRemoveSelectedDealer(value: string) {
        // We update the draft here too. the draft #1 ONLY so we dont really perform network request when user tries to remove a tag
        this.createUserStepsService.updateStepProperty(
            this.selectedUserType()?.id as SelectDealerStepRoles,
            'select-dealer',
            'selectedDealers',
            (this.draftData() as Partial<SelectDealerStepSchema>)?.['select-dealer']?.[
                'selectedDealers'
            ]?.filter((dealer) => dealer.value !== value) ?? [],
        );
    }

    /**
     * @protected
     * @description Navigates back to the input-information step.
     * @returns {void}
     */
    protected onGoToInputInformation() {
        this.createUserStepsService.goToStep('input-information');
    }
}
